<template>
	<div class="card">
		<div
			v-if="loyaltyProfile.level"
			class="card-header"
			:style="{
				backgroundColor: cardBackground,
			}"
		>
			<h3>
				<span class="font-weight-bolder">{{ loyaltyProfile.level.name }}</span> уровень
			</h3>
			<p v-if="!isAllowedPremiumPointsCharged" class="mb-0">
				Вы можете оплатить баллами до
				{{ Math.round(loyaltyProfile.level.max_payment_percent) }}%
				стоимости
			</p>
		</div>
		<div class="card-body">
			<!-- Списание премиальных баллов -->
			<template v-if="isAllowedPremiumPointsCharged">
				<div class="mb-4 pb-3 border-bottom">
					<b-form-checkbox
						v-if="!chargedPremiumPoints"
						v-model="enablePremiumPoints"
						switch
						:value="1"
						:unchecked-value="0"
						class="mb-3"
						name="showPremiumPointsCharging"
						@change="$emit('updatePoints')"
					>
						Оплатить <b>премиальными баллами</b>
					</b-form-checkbox>
					<div v-show="enablePremiumPoints">
						<label
							v-if="!chargedPremiumPoints"
							for="loyalty_premium_points_to_charge"
							class="mb-3"
						>
							Выберите количество баллов для списания.
							<span
								id="special-info-tooltip"
								class="badge badge-secondary badge-pill"
								tabindex="0"
							>i</span>
							<b-tooltip target="special-info-tooltip">
								<div>
									Премиальными баллами можно оплатить до 100% стоимости
									образовательной программы независимо от уровня участия.
								</div>
							</b-tooltip>
						</label>
						<div class="container row pr-0">
							<b-form-input
								id="loyalty_premium_points_to_charge"
								v-model="premiumPoints"
								type="range"
								label="Выберите количество баллов для списания"
								name="loyalty_premium_points_to_charge"
								step="10"
								min="0"
								:max="maxPremiumPointsTotal"
								class="col col-9 col-sm-9 pr-2"
								:disabled="isPremiumPointsDisabled"
							/>
							<b-form-input
								id="loyalty_premium_points_input"
								v-model="premiumPoints"
								:max="maxPremiumPointsTotal"
								type="number"
								name="premium_points"
								class="form-control col col-2 col-sm-3 rounded-0 required"
								:disabled="isPremiumPointsDisabled"
								required
							/>
						</div>
						<small v-if="!isPremiumPointsDisabled" :class="{ 'text-secondary': isLoadingPoints }">
							Доступно: {{ maxPremiumPointsTotal }} баллов.
						</small>
						<div class="mt-3">
							<span v-if="!chargedPremiumPoints">Будет</span> списано:
							<strong> {{ premiumPoints }} баллов.</strong>
						</div>
					</div>
				</div>
			</template>

			<!-- Списание активных баллов -->
			<template v-if="isAllowedActivePointsCharged">
				<div>
					<b-form-checkbox
						v-if="!chargedPoints"
						:key="`showActivePointsCharging-${enableActivePoints}`"
						v-model="enableActivePoints"
						switch
						:value="1"
						:unchecked-value="0"
						name="showActivePointsCharging"
						class="mb-3"
					>
						Оплатить <b>активными баллами</b>
					</b-form-checkbox>
					<div v-show="enableActivePoints">
						<label
							v-if="!chargedPoints"
							for="loyalty_active_points_to_charge"
							class="mb-3"
						>
							Выберите количество баллов для списания.
							<span
								id="active-info-tooltip"
								class="badge badge-secondary badge-pill"
								tabindex="0"
							>i</span>
							<b-tooltip target="active-info-tooltip">
								<div>
									Активные баллы можно использовать
									для оплаты части стоимости следующих покупок.
								</div>
							</b-tooltip>
						</label>
						<div class="container row pr-0">
							<b-form-input
								id="loyalty_active_points_to_charge"
								v-model="activePoints"
								type="range"
								label="Выберите количество активных баллов для списания"
								name="loyalty_active_points_to_charge"
								step="10"
								min="0"
								:max="maxActivePointsTotal"
								class="col col-9 col-sm-9 pr-2"
								:disabled="isActivePointsDisabled"
							/>
							<b-form-input
								id="loyalty_active_points_input"
								v-model="activePoints"
								:max="maxActivePointsTotal"
								type="number"
								name="points"
								class="form-control col col-2 col-sm-3 rounded-0 required"
								:disabled="isActivePointsDisabled"
								required
							/>
						</div>
						<small v-if="!isActivePointsDisabled" :class="{ 'text-secondary': isLoadingPoints }">
							Доступно: {{ maxActivePointsTotal }} баллов.
						</small>
						<div
							v-if="enableActivePoints && isAllowedActivePointsCharged"
							class="mt-3"
						>
							<span v-if="!chargedPoints">Будет</span> списано:
							<strong>{{ activePoints }} баллов.</strong>
						</div>
					</div>
				</div>
			</template>
			<div
				v-if="isAllowedToEarn && allTumblersDisabled"
				class="points-table"
			>
				Вам будет начислено {{ awardedPoints }} активных баллов.
			</div>
		</div>
	</div>
</template>

<script>
import { LOYALTY_LEVELS } from '~/utils/dictionaries';
import { amountToFixed } from '~/utils/intl-converter';

/**
 * Usage: <charge-points v-model="loyaltyData" price="4000" />
 *
 * loyaltyData will be like {
 *   needChargePoints = 0 || 1
 *   pointsToCharge = needChargePoints ? (> 0) : 0
 * }
 *
 * TODO: design
 */
export default {
	name: 'ChargePoints',
	props: {
		price: {
			type: Number,
			required: true,
		},
		awardedPoints: {
			type: Number,
			default: 0,
		},
		chargedPoints: {
			type: Number,
			default: 0,
		},
		chargedPremiumPoints: {
			type: Number,
			default: 0,
		},
		loyaltyProfile: {
			type: Object,
			default: () => ({}),
		},
		cardBackground: {
			type: String,
			default: '',
		},
		selectedProducts: {
			type: Array,
			default: () => ([]),
		},
		allProductsFromGroup: {
			type: Array,
			default: () => ([]),
		},
		isAllowedActivePointsCharged: {
			type: Boolean,
			default: false,
		},
		isAllowedToEarn: {
			type: Boolean,
			default: false,
		},
		isLoadPoints: {
			type: Boolean,
			default: false,
		},
		isPromoCodeUsed: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			levels: LOYALTY_LEVELS,
			maxActivePointsTotal: null,
			maxPremiumPointsTotal: null,
			maxActivePointsCurrent: null,
			maxPremiumPointsCurrent: null,
			enableActivePoints: 0,
			enablePremiumPoints: 0,
			activePoints: 0,
			premiumPoints: 0,
			isChargePointsLoading: false,
			productPointsMap: {},
		};
	},
	computed: {
		user() {
			return this.$auth.user;
		},
		amount() {
			if (this.chargedPoints) return this.price;
			return this.price - this.activePoints - this.premiumPoints;
		},
		isAllowedPremiumPointsCharged() {
			return Boolean(Number(this.loyaltyProfile.premium_points));
		},
		allTumblersDisabled() {
			return !this.enableActivePoints && !this.enablePremiumPoints;
		},
		isLoadingPoints() {
			return this.isLoadPoints;
		},
		isActivePointsDisabled() {
			return this.chargedPoints || this.isLoadingPoints;
		},
		isPremiumPointsDisabled() {
			return this.chargedPremiumPoints || this.isLoadingPoints;
		},
	},
	watch: {
		enableActivePoints: {
			handler(value) {
				if (value) {
					this.$emit('updatePoints');
				} else {
					this.$emit('hidePoints');
				}

				this.$emit('input', this.formData());

				if (value) {
					this.calculateMaxActivePoints();
				} else {
					this.activePoints = 0;
				}
			},
			immediate: true,
		},
		enablePremiumPoints: {
			handler(value) {
				this.$emit('input', this.formData());

				if (value) {
					this.calculateMaxPremiumPoints();
				} else {
					this.premiumPoints = 0;
				}
			},
			immediate: true,
		},
		activePoints: {
			handler() {
				this.calculateMaxPremiumPoints();
			},
		},
		premiumPoints: {
			handler() {
				this.calculateMaxActivePoints();
			},
		},
		price: {
			handler() {
				if (this.enableActivePoints) {
					this.calculateMaxActivePoints();

					this.activePoints = 0;
				}
				if (this.enablePremiumPoints) {
					this.calculateMaxPremiumPoints();

					this.premiumPoints = 0;
				}
			},
		},
		isPromoCodeUsed: {
			handler(value) {
				if (value) this.enableActivePoints = 0;
			},
		},
		chargedPoints: {
			handler(value) {
				if (value) {
					this.enableActivePoints = 1;
					this.activePoints = value;
				}
			},
			immediate: true,
		},
		chargedPremiumPoints: {
			handler(value) {
				if (value) {
					this.enablePremiumPoints = 1;
					this.premiumPoints = value;
				}
			},
			immediate: true,
		},
	},
	async mounted() {
		await this.getLoyaltyLevels();
	},
	methods: {
		async getLoyaltyLevels() {
			const {
				data: levels,
				first_program_trigger_threshold: firstProgramThreshold,
			} = await this.$api.getLoyaltyLevels();

			if (firstProgramThreshold) {
				this.firstProgramThreshold = firstProgramThreshold;
			}

			this.levels = levels.map((item) => ({
				...this.levels.find(
					(level) => level.id === item.id,
				),
				...item,
			}));
		},
		async calculateMaxActivePoints() {
			if (!this.$auth.user) {
				return;
			}

			try {
				this.isChargePointsLoading = true;

				this.maxActivePointsCurrent = 0;
				const pointsLeft = this.price - this.premiumPoints;

				const products = this.selectedProducts?.length > 0
					? this.selectedProducts
					: this.allProductsFromGroup;

				// todo Сделать через одну транзакцию со множеством продуктов.
				// Суммирование списываемых баллов, как сейчас, ведет к тому, что если у участника
				// ограниченное количество баллов и на форме несколько разных продуктов, количество
				// доступных для списания баллов на форме умножается на количество разных продуктов и
				// становится совершенно недостоверным. Внизу создал костыль, чтобы этого не было.
				await Promise.all(products.map(async (product) => {
					const points = await this.getProductMaxPoints(product);

					this.maxActivePointsCurrent += points;
				}));

				if (products.length > 1) {
					this.maxActivePointsCurrent = amountToFixed(Math.min(
						this.maxActivePointsCurrent,
						this.loyaltyProfile.points,
						pointsLeft,
					));
				} else {
					this.maxActivePointsCurrent = amountToFixed(Math.min(
						this.maxActivePointsCurrent,
						pointsLeft,
					));
				}

				if (!this.maxActivePointsTotal) {
					this.maxActivePointsTotal = this.maxActivePointsCurrent;
				}
			} finally {
				this.isChargePointsLoading = false;

				if (this.activePoints > this.maxActivePointsCurrent) {
					this.activePoints = this.maxActivePointsCurrent;
				}
				if (this.chargedPoints) {
					this.activePoints = this.chargedPoints;
				}

				this.$emit('input', this.formData());
			}
		},
		async calculateMaxPremiumPoints() {
			if (!this.$auth.user) {
				return;
			}

			try {
				this.isChargePointsLoading = true;
				this.maxPremiumPointsCurrent = 0;
				const products = this.selectedProducts?.length > 0
					? this.selectedProducts
					: this.allProductsFromGroup;

				await Promise.all(products.map(async () => {
					let maxPremiumPoints = Number(this.price) - Number(this.activePoints);

					maxPremiumPoints = Math.min(
						maxPremiumPoints,
						this.loyaltyProfile.premium_points,
					);

					this.maxPremiumPointsCurrent += amountToFixed(maxPremiumPoints);

					// init available points
					if (!this.maxPremiumPointsTotal) {
						this.maxPremiumPointsTotal = this.maxPremiumPointsCurrent;
					}
				}));
			} finally {
				this.isChargePointsLoading = false;

				if (this.premiumPoints > this.maxPremiumPointsCurrent) {
					this.premiumPoints = this.maxPremiumPointsCurrent;
				}

				this.$emit('input', this.formData());
			}
		},
		formData() {
			return {
				needChargePoints: this.enableActivePoints,
				needChargePremiumPoints: this.enablePremiumPoints,
				pointsToCharge: this.activePoints,
				premiumPointsToCharge: this.premiumPoints,
				actualAmount: this.amount,
				showActivePointsCharging: this.enableActivePoints,
				showPremiumPointsCharging: this.enablePremiumPoints,
			};
		},
		async getProductMaxPoints(product) {
			if (this.productPointsMap[product.id]) {
				return this.productPointsMap[product.id];
			}

			const productQuantity = parseFloat(product.quantity ?? '1') || 0;
			const transactionInfo = await this.$api.getTransactionInfoByProgram(
				product.id,
				product.price * productQuantity,
				true,
			);
			const points = Math.abs(parseFloat(transactionInfo.points));
			this.productPointsMap[product.id] = points;

			return points;
		},
	},
};
</script>

<style lang="scss" scoped>
@import "assets/styles/sk_variables";
// For use bootstrap vars and mixins inside
@import "assets/styles/bootstrap/scss/functions";
@import "assets/styles/bootstrap/scss/variables";
@import "assets/styles/bootstrap/scss/mixins";

.points-table {
	border: 1px solid $blue;
	padding: 0.5rem 1rem;
	margin-top: 0.5rem;
	border-radius: 0.7rem;
	max-width: 13rem;
}
.custom-range {
	height: 2.2rem;
}
.custom-switch {
	padding-left: 2.25rem;
}
// Скрытие стандартных стрелок
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

input[type="number"] {
	-moz-appearance: textfield;
}
</style>
